<template>
  <div class="form-wrapper">
    <h2>Benachrichtigungen aktivieren</h2>

    <!-- Erklärungstext für den Benutzer -->
    <p class="intro">
      Damit wir dich auch im Hintergrund oder bei geschlossenem Tab über Wettergebnisse informieren können,
      benötigen wir deine Erlaubnis für Browser-Benachrichtigungen.
    </p>

    <!-- Erfolgsmeldung, wenn Berechtigungen bereits erteilt wurden -->
    <div v-if="permission === 'granted'" class="success-box">
      <strong>Benachrichtigungen sind aktiviert.</strong>
      Du wirst über neue Nachrichten informiert.
    </div>

    <!-- Fehlermeldung, wenn Berechtigungen blockiert wurden -->
    <div v-else-if="permission === 'denied'" class="error-box">
      <strong>Benachrichtigungen sind blockiert.</strong>
      Bitte aktiviere sie in den Browser-Einstellungen für diese Website.
    </div>

    <!-- Standardmeldung, wenn noch keine Entscheidung getroffen wurde -->
    <div v-else class="warning-box">
      <strong>Benachrichtigungen sind noch nicht erlaubt.</strong>
      Bitte erlaube Benachrichtigungen, damit wir dich über neue Nachrichten informieren können.
    </div>

    <!-- Button, um die Berechtigungsanfrage zu starten -->
    <button v-if="permission !== 'granted'" type="button" @click="requestNotificationPermission" :disabled="loading">
      {{ loading ? 'Wird angefragt...' : 'Benachrichtigungen erlauben' }}
    </button>

    <!-- Anzeigen von Info- oder Fehlermeldungen nach der Anfrage -->
    <p v-if="infoMessage" class="info">{{ infoMessage }}</p>
    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router'

// Lokale Zustandsvariablen
const loading = ref(false)
const errorMessage = ref('')
const infoMessage = ref('')

// Der aktuelle Berechtigungsstatus ('default', 'granted', 'denied')
const permission = ref('default')
// Prüft, ob die Notification API im Browser überhaupt unterstützt wird.
const isNotificationSupported = computed(() => typeof window !== 'undefined' && 'Notification' in window);

onMounted(() => {
  if (isNotificationSupported.value) permission.value = Notification.permission
  else {
    permission.value = 'unsupported'
    errorMessage.value = 'Dein Browser unterstützt keine Benachrichtigungen.'
  }
})

/**
 * Startet die Anfrage für die Benachrichtigungsberechtigung über die Browser-API.
 */
const requestNotificationPermission = async () => {
  errorMessage.value = ''
  infoMessage.value = ''

  if (!isNotificationSupported.value) {
    errorMessage.value = 'Benachrichtigungen werden von diesem Browser nicht unterstützt.'
    return
  }

  loading.value = true

  try {
    const result = await Notification.requestPermission();
    // Aktualisiert den lokalen Zustand mit dem Ergebnis der Anfrage.
    permission.value = result

    if (result === 'granted') {
      infoMessage.value = 'Danke! Benachrichtigungen sind jetzt aktiviert.'
      setTimeout(()=>{
        const router = useRouter();
        router.push('/');
      }, 1000);
    } else if (result === 'denied') {
      errorMessage.value = 'Benachrichtigungen wurden abgelehnt.'
    } else {
      infoMessage.value = 'Die Anfrage für Benachrichtigungen wurde geschlossen.'
    }
  } catch (error) {
    errorMessage.value = error?.message || 'Die Berechtigung konnte nicht angefragt werden.'
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.form-wrapper {
  max-width: 500px;
  margin: 2rem auto;
  padding: 2rem;
  background: $card-background;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);

  h2 {
    margin-top: 0;
    color: $secondary-color;
    text-align: center;
    margin-bottom: 1.5rem;
  }

  .intro {
    margin-bottom: 1.5rem;
    line-height: 1.6;
    color: $text-color;
    text-align: center;
  }

  button {
    width: 100%;
    padding: 0.75rem;
    border: none;
    border-radius: 4px;
    background-color: $primary-color;
    color: white;
    font-size: 1rem;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background-color: $primary-color-hover;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .success-box,
  .warning-box,
  .error-box {
    margin-bottom: 1rem;
    padding: 1rem;
    border-radius: 8px;
    line-height: 1.5;
  }

  .success-box {
    background: rgba(25, 135, 84, 0.12);
    color: #198754;
    border: 1px solid rgba(25, 135, 84, 0.25);
  }

  .warning-box {
    background: rgba(255, 193, 7, 0.12);
    color: #9a6700;
    border: 1px solid rgba(255, 193, 7, 0.25);
  }

  .error-box {
    background: rgba(220, 53, 69, 0.12);
    color: #dc3545;
    border: 1px solid rgba(220, 53, 69, 0.25);
  }

  .info {
    margin-top: 1rem;
    color: $text-color;
    text-align: center;
  }

  .error {
    margin-top: 1rem;
    color: #dc3545;
    text-align: center;
  }
}
</style>